/**
 * SOLID Review
 * S — Strong. One concern: the client's ONE serial section — load, decide, save, keep — shared by every
 *     context that writes the client's state.
 * O — Good. A new context takes the same steps; nothing here changes.
 * L — N/A — no inheritance hierarchy.
 * I — Good. Two steps — one for a table decision, one for a session decision — the outcome a use case answers, and the
 *     two steps as objects for the collaborators that run them.
 * D — Strong. Two port interfaces, injected, the projection of a table decision's input, and the ports' Result.
 */
import type { IContextRequest } from '../../domain/ports/schemas/ContextRequest';
import type { IPropsClientTableInput } from '../../domain/ports/schemas/ClientTable';
import type { IPropsClientChanges, IPropsClientInput } from '../../domain/ports/schemas/ClientChanges';
import type { IPortClientState, IPortClientTabStorage } from '../ports/contracts';
import type { TPortResult, TPortOutcome } from '../ports/PortResult';
import { ClientTableProjector } from '../state/ClientTableProjector';

/** What a decision returns through its port. */
export type TDecision<D> = TPortResult<{ readonly changes: IPropsClientChanges; readonly dto: D }>;

/** A void use case done. */
export const DONE: TPortOutcome = { success: true, value: undefined };

/**
 * One kind of step of the serial section, as an object a collaborator is handed (frontend-application-oo P2): over the input
 * its decisions read — the table's (`onTable`), or the whole client's — and the outcome a use case answers.
 */
export interface IServiceSerialStep<TInput> {
  run<D>(ctx: IContextRequest, decide: (input: TInput) => Promise<TDecision<D>>): Promise<TPortResult<D>>;
  outcome(result: TPortResult<unknown> | TPortOutcome): TPortOutcome;
}

export interface IConfigDepClientSteps {
  readonly state: IPortClientState;
  readonly tabStorage: IPortClientTabStorage;
}

/**
 * THE SERIAL SECTION (frontend strict-ports plan, FE1; shared since FE2, 2026-10-03). Ports are
 * asynchronous, so two steps could interleave — load, load, decide, decide, save, save — and the second
 * save would erase the first's changes, from a state read before them. Every step therefore runs on one
 * queue, and nothing inside it awaits the network. A server call sits BETWEEN two steps, never inside
 * one, so a slow server cannot hold the client.
 *
 * FE1 kept this queue inside TableContext. The session writes the same stores — a seat taken by a code
 * is a table change too — so a second queue would let the two contexts erase each other. There is one,
 * here, handed to both.
 *
 * A step that saves changes also keeps what the decision says to keep (`persist`) through the tab's
 * storage, in the same step: a seat taken is a game remembered, with no effect watching for it.
 */
export class ClientSteps {
  private tail: Promise<void> = Promise.resolve();

  /** The table decisions' step, for a collaborator that runs them (`ServerRoundTrip`, `DecisionStep`). */
  readonly onTable: IServiceSerialStep<IPropsClientTableInput> = {
    run: <D>(ctx: IContextRequest, decide: (input: IPropsClientTableInput) => Promise<TDecision<D>>) => this.table(ctx, decide),
    outcome: (result) => this.outcome(result),
  };

  /** The session decisions' step — the table and the session — for the same collaborators. */
  readonly onSession: IServiceSerialStep<IPropsClientInput> = {
    run: <D>(ctx: IContextRequest, decide: (input: IPropsClientInput) => Promise<TDecision<D>>) => this.session(ctx, decide),
    outcome: (result) => this.outcome(result),
  };

  private readonly tables = new ClientTableProjector();

  constructor(private readonly deps: IConfigDepClientSteps) {}

  private serial<T>(work: () => Promise<T>): Promise<T> {
    const run = this.tail.then(work);
    this.tail = run.then(() => undefined, () => undefined);
    return run;
  }

  /** A table decision's step: it reads the table half of the snapshot, and the rules the session holds (frontend-domain-oo P2). */
  table<D>(ctx: IContextRequest, decide: (input: IPropsClientTableInput) => Promise<TDecision<D>>): Promise<TPortResult<D>> {
    return this.run(ctx, (loaded) => decide(this.tables.inputOf(loaded)));
  }

  /** A session decision's step: it reads the table and the session. */
  session<D>(ctx: IContextRequest, decide: (input: IPropsClientInput) => Promise<TDecision<D>>): Promise<TPortResult<D>> {
    return this.run(ctx, decide);
  }

  /** What a use case answers for a step or a call it ends on: done, or the failure as it came — whatever value it carried. */
  outcome(result: TPortResult<unknown> | TPortOutcome): TPortOutcome {
    return result.success ? DONE : result;
  }

  /** Load, decide, save what changed, keep what the decision keeps — one indivisible step. */
  private run<D>(ctx: IContextRequest, decide: (input: IPropsClientInput) => Promise<TDecision<D>>): Promise<TPortResult<D>> {
    return this.serial(async (): Promise<TPortResult<D>> => {
      const loaded = await this.deps.state.snapshot(ctx);
      if (!loaded.success) return loaded;
      const decided = await decide(loaded.value);
      if (!decided.success) return decided;
      const { changes, dto } = decided.value;
      if (Object.keys(changes).length > 0) {
        const saved = await this.deps.state.apply(ctx, changes);
        if (!saved.success) return saved;
      }
      if (changes.persist !== undefined) {
        const kept = await this.deps.tabStorage.persist(ctx, changes.persist);
        if (!kept.success) return kept;
      }
      return { success: true, value: dto };
    });
  }
}
